import {
  computeAnswerUpsertPlan,
  dedupePreserveOrder,
  normalizeValueTyped,
} from "../../../api/src/users/answers/upsert-rules";

interface QuestionOptionLike {
  questionOptionId: string;
}

interface AnswerLike {
  id: string;
  valueTyped: string | null;
  updatedAt: Date | string;
  answerQuestionOptions: QuestionOptionLike[];
}

export interface OptimisticQuestionMarker {
  rtkEditId: string;
  updatedAt: string;
}

export interface OptimisticQuestionCacheFields {
  __optimisticMarker?: OptimisticQuestionMarker;
}

interface QuestionLike<TAnswer extends AnswerLike> {
  id: string;
  parentQuestionId: string | null;
  questionType: string | null;
  answers: TAnswer[];
  __optimisticMarker?: OptimisticQuestionMarker;
}

interface GroupLike<TQuestion> {
  questions: TQuestion[];
}

interface OptimisticCreateParams {
  questionId: string;
  normalizedValueTyped: string | null;
  normalizedOptionIds: string[];
  now: Date;
}

function collectDescendantQuestionIds<
  TAnswer extends AnswerLike,
  TQuestion extends QuestionLike<TAnswer>,
  TGroup extends GroupLike<TQuestion>,
>(params: { draft: TGroup[]; parentQuestionId: string }): string[] {
  const questions = params.draft.flatMap((g) => g.questions);
  const childrenByParentId = new Map<string, string[]>();

  questions.forEach((q) => {
    if (!q.parentQuestionId) return;
    const list = childrenByParentId.get(q.parentQuestionId) ?? [];
    list.push(q.id);
    childrenByParentId.set(q.parentQuestionId, list);
  });

  const stack: string[] = childrenByParentId.get(params.parentQuestionId)
    ? [...(childrenByParentId.get(params.parentQuestionId) ?? [])]
    : [];
  const descendantIds: string[] = [];

  while (stack.length > 0) {
    const currentId = stack.pop();
    if (!currentId) continue;

    descendantIds.push(currentId);
    const children = childrenByParentId.get(currentId) ?? [];
    children.forEach((childId) => stack.push(childId));
  }

  return descendantIds;
}

export function clearDescendantAnswers<
  TAnswer extends AnswerLike,
  TQuestion extends QuestionLike<TAnswer>,
  TGroup extends GroupLike<TQuestion>,
>(params: { draft: TGroup[]; parentQuestionId: string }): number {
  const descendantIds = collectDescendantQuestionIds(params);
  const questionById = new Map(
    params.draft.flatMap((g) => g.questions).map((q) => [q.id, q] as const),
  );

  let clearedCount = 0;
  for (const id of descendantIds) {
    const question = questionById.get(id);
    if (question) {
      question.answers = [];
      clearedCount += 1;
    }
  }

  return clearedCount;
}

export function clearQuestionAnswerTreeInCache<
  TAnswer extends AnswerLike,
  TQuestion extends QuestionLike<TAnswer>,
  TGroup extends GroupLike<TQuestion>,
>(params: { draft: TGroup[]; parentQuestionId: string }): void {
  const { draft, parentQuestionId } = params;

  const question = draft
    .flatMap((g) => g.questions)
    .find((q) => q.id === parentQuestionId);
  if (!question) return;

  question.answers = [];
  clearDescendantAnswers({ draft, parentQuestionId });
}

export function markQuestionsOptimistic<
  TAnswer extends AnswerLike,
  TQuestion extends QuestionLike<TAnswer>,
  TGroup extends GroupLike<TQuestion>,
>(params: {
  draft: TGroup[];
  questionIds: string[];
  marker: OptimisticQuestionMarker;
}): void {
  const questionById = new Map(
    params.draft
      .flatMap((group) => group.questions)
      .map((question) => [question.id, question] as const),
  );

  params.questionIds.forEach((questionId) => {
    const question = questionById.get(questionId);
    if (question) {
      question.__optimisticMarker = params.marker;
    }
  });
}

export function clearOptimisticQuestionMarkers<
  TAnswer extends AnswerLike,
  TQuestion extends QuestionLike<TAnswer>,
  TGroup extends GroupLike<TQuestion>,
>(params: { draft: TGroup[]; questionIds: string[]; rtkEditId: string }): void {
  const questionById = new Map(
    params.draft
      .flatMap((group) => group.questions)
      .map((question) => [question.id, question] as const),
  );

  params.questionIds.forEach((questionId) => {
    const question = questionById.get(questionId);
    if (question?.__optimisticMarker?.rtkEditId === params.rtkEditId) {
      delete question.__optimisticMarker;
    }
  });
}

export function areQuestionsMarkedOptimistic<
  TAnswer extends AnswerLike,
  TQuestion extends QuestionLike<TAnswer>,
  TGroup extends GroupLike<TQuestion>,
>(params: {
  draft: TGroup[];
  questionIds: string[];
  rtkEditId: string;
}): boolean {
  const questionById = new Map(
    params.draft
      .flatMap((group) => group.questions)
      .map((question) => [question.id, question] as const),
  );

  return params.questionIds.every(
    (questionId) =>
      questionById.get(questionId)?.__optimisticMarker?.rtkEditId ===
      params.rtkEditId,
  );
}

export function mergeQuestionGroupsWithOptimisticMarkers<
  TAnswer extends AnswerLike,
  TQuestion extends QuestionLike<TAnswer>,
  TGroup extends GroupLike<TQuestion>,
>(params: { currentGroups: TGroup[]; incomingGroups: TGroup[] }): TGroup[] {
  const currentQuestionsById = new Map(
    params.currentGroups
      .flatMap((group) => group.questions)
      .map((question) => [question.id, question] as const),
  );

  return params.incomingGroups.map((incomingGroup) => ({
    ...incomingGroup,
    questions: incomingGroup.questions.map((incomingQuestion) => {
      const currentQuestion = currentQuestionsById.get(incomingQuestion.id);
      const marker = currentQuestion?.__optimisticMarker;
      if (!currentQuestion || !marker) {
        return incomingQuestion;
      }

      return {
        ...incomingQuestion,
        answers: currentQuestion.answers,
        __optimisticMarker: marker,
      };
    }),
  }));
}

export function updateQuestionAnswerTreeInCache<
  TAnswer extends AnswerLike,
  TQuestion extends QuestionLike<TAnswer>,
  TGroup extends GroupLike<TQuestion>,
>(params: {
  draft: TGroup[];
  questionId: string;
  valueTyped: string | null | undefined;
  questionOptionIds: string[] | undefined;
  forceCleanSlate: boolean;
  optimisticNow: Date;
  createOptimisticAnswer: (params: OptimisticCreateParams) => TAnswer;
  createAnswerQuestionOption: (params: {
    answerId: string;
    optionId: string;
    now: Date;
  }) => TAnswer["answerQuestionOptions"][number];
}): { affectedQuestionIds: string[] } {
  const {
    draft,
    questionId,
    valueTyped,
    questionOptionIds,
    forceCleanSlate,
    optimisticNow,
    createOptimisticAnswer,
    createAnswerQuestionOption,
  } = params;

  const question = draft
    .flatMap((g) => g.questions)
    .find((q) => q.id === questionId);
  if (!question) return { affectedQuestionIds: [] };

  const existingAnswer = question.answers[0];
  const currentOptionIds =
    existingAnswer?.answerQuestionOptions.map((o) => o.questionOptionId) ?? [];

  const plan = computeAnswerUpsertPlan({
    questionType: question.questionType,
    hasExistingAnswer: Boolean(existingAnswer),
    currentValueTyped: existingAnswer?.valueTyped,
    currentOptionIds,
    nextValueTyped: valueTyped,
    nextOptionIds: questionOptionIds,
    forceCleanSlate,
  });

  switch (plan.kind) {
    case "no-op": {
      if (!existingAnswer) {
        question.answers = [];
      }
      break;
    }
    case "create": {
      const normalizedOptionIds = dedupePreserveOrder(questionOptionIds ?? []);
      const { normalizedValueTyped } = normalizeValueTyped(valueTyped);

      question.answers = [
        createOptimisticAnswer({
          questionId,
          normalizedValueTyped,
          normalizedOptionIds,
          now: optimisticNow,
        }),
      ];
      break;
    }
    case "delete": {
      question.answers = [];
      break;
    }
    case "update": {
      if (existingAnswer) {
        if (plan.valueTypedUpdate.kind === "set") {
          existingAnswer.valueTyped = plan.valueTypedUpdate.value;
        }
        existingAnswer.updatedAt = optimisticNow.toISOString();
        existingAnswer.answerQuestionOptions = plan.normalizedOptionIds.map(
          (optionId) =>
            createAnswerQuestionOption({
              answerId: existingAnswer.id,
              optionId,
              now: optimisticNow,
            }),
        );
      }
      break;
    }
  }

  let descendantIds: string[] = [];
  if (plan.willDeleteChildren) {
    descendantIds = collectDescendantQuestionIds({
      draft,
      parentQuestionId: questionId,
    });
    const questionById = new Map(
      draft.flatMap((g) => g.questions).map((q) => [q.id, q] as const),
    );
    for (const id of descendantIds) {
      const descendant = questionById.get(id);
      if (descendant) {
        descendant.answers = [];
      }
    }
  }

  return {
    affectedQuestionIds: [questionId, ...descendantIds],
  };
}
